<script lang="ts">
export default {
  name: 'DatePicker',
};
</script>

<script setup lang="ts">
import { type DateValue } from '@internationalized/date';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@lucide/vue';
import {
  DatePickerArrow,
  DatePickerCalendar,
  DatePickerCell,
  DatePickerCellTrigger,
  DatePickerContent,
  DatePickerField,
  DatePickerGrid,
  DatePickerGridBody,
  DatePickerGridHead,
  DatePickerGridRow,
  DatePickerHeadCell,
  DatePickerHeader,
  DatePickerHeading,
  DatePickerInput,
  DatePickerPrev,
  DatePickerNext,
  DatePickerRoot,
  DatePickerTrigger,
} from 'reka-ui';

const date = defineModel<DateValue>();

defineOptions({
  inheritAttrs: false,
});
</script>

<template>
  <DatePickerRoot v-model="date">
    <DatePickerField
      v-slot="{ segments }"
      class="input input-sm flex items-center justify-between bg-white text-center text-primary select-none data-[invalid]:border-red-500"
      v-bind="$attrs">
      <div class="flex items-center">
        <template v-for="item in segments" :key="item.part">
          <DatePickerInput v-if="item.part === 'literal'" :part="item.part" class="text-gray-500">
            {{ item.value }}
          </DatePickerInput>
          <DatePickerInput
            v-else
            :part="item.part"
            class="rounded-md p-0.5 focus:shadow-[0_0_0_1px] focus:shadow-gray-500 focus:outline-none data-[placeholder]:text-gray-500">
            {{ item.value }}
          </DatePickerInput>
        </template>
      </div>

      <DatePickerTrigger class="rounded-md p-1 text-xl focus:shadow-[0_0_0_1px] focus:shadow-gray-500">
        <CalendarIcon :size="17" />
      </DatePickerTrigger>
    </DatePickerField>
    <Teleport to="body">
      <DatePickerContent
        :side-offset="4"
        class="data-[state=open]:data-[side=bottom]:animate-slide-down-and-fade z-1000 rounded-xl bg-white shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)] will-change-[transform,opacity] focus:shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)]">
        <DatePickerArrow class="fill-white" />
        <DatePickerCalendar v-slot="{ weekDays, grid }" class="p-4">
          <DatePickerHeader class="flex items-center justify-between">
            <DatePickerPrev
              class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[9px] bg-transparent text-gray-500 transition-all hover:bg-primary/80 hover:text-white focus:shadow-[0_0_0_1px] focus:shadow-gray-500 active:scale-98 active:transition-all">
              <ChevronLeftIcon :size="17" />
            </DatePickerPrev>

            <DatePickerHeading class="font-medium text-black" />
            <DatePickerNext
              class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[9px] bg-transparent text-gray-500 transition-all hover:bg-primary/80 hover:text-white focus:shadow-[0_0_0_1px] focus:shadow-gray-500 active:scale-98 active:transition-all">
              <ChevronRightIcon :size="17" />
            </DatePickerNext>
          </DatePickerHeader>
          <div class="flex flex-col space-y-4 pt-4 sm:flex-row sm:space-y-0 sm:space-x-4">
            <DatePickerGrid
              v-for="month in grid"
              :key="month.value.toString()"
              class="w-full border-collapse space-y-1 select-none">
              <DatePickerGridHead>
                <DatePickerGridRow class="mb-1 flex w-full justify-between">
                  <DatePickerHeadCell
                    v-for="day in weekDays"
                    :key="day"
                    class="w-8 rounded-md text-xs text-primary">
                    {{ day }}
                  </DatePickerHeadCell>
                </DatePickerGridRow>
              </DatePickerGridHead>
              <DatePickerGridBody>
                <DatePickerGridRow
                  v-for="(weekDates, index) in month.rows"
                  :key="`weekDate-${index}`"
                  class="flex w-full">
                  <DatePickerCell v-for="weekDate in weekDates" :key="weekDate.toString()" :date="weekDate">
                    <DatePickerCellTrigger
                      :day="weekDate"
                      :month="month.value"
                      class="relative flex h-8 w-8 items-center justify-center rounded-[9px] border border-transparent bg-transparent text-sm font-normal whitespace-nowrap text-black outline-none before:absolute before:top-[5px] before:hidden before:h-1 before:w-1 before:rounded-full before:bg-white hover:border-primary focus:shadow-[0_0_0_1px] data-[disabled]:text-black/30 data-[selected]:bg-secondary data-[selected]:font-medium data-[selected]:text-white data-[selected]:before:bg-white data-[today]:before:block data-[today]:before:bg-green-500 data-[unavailable]:pointer-events-none data-[unavailable]:text-black/30 data-[unavailable]:line-through" />
                  </DatePickerCell>
                </DatePickerGridRow>
              </DatePickerGridBody>
            </DatePickerGrid>
          </div>
        </DatePickerCalendar>
      </DatePickerContent>
    </Teleport>
  </DatePickerRoot>
</template>
